import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../../context/notificationContext";
import useI18n from "../../../../../../hooks/useTranslations";
import { useApi } from "../../../../../../http/api";
import {
  AdminWallMaterial,
  UpdateWallMaterialNewImageBody,
  UpdateWallMaterialRemoveImageBody,
} from "../../../../../../http/vertigrip/types/wallMaterials";
import { useWallMaterialsApi } from "../../../../../../http/vertigrip/useWallMaterials";
import Form from "../../../../../ui/Form";
import FormField from "../../../../../ui/FormField";
import FormFieldsContainer from "../../../../../ui/FormFieldsContainer";
import { default as useImageUploader } from "../../../../../ui/ImageUploader";
import Input from "../../../../../ui/Input";
import Numeric from "../../../../../ui/Numeric";
import TranslatableField from "../../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";

interface UpdateWallMaterialForm {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface WallMaterialDetailsProps {
  wallMaterial?: AdminWallMaterial;
  disabled?: boolean;
}

const WallMaterialDetails: FC<WallMaterialDetailsProps> = ({ wallMaterial, disabled }: WallMaterialDetailsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const wallMaterialApi = useWallMaterialsApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: wallMaterial?.image,
    isLoading: !wallMaterial,
    disabled,
  });

  const defaultValues = {
    name: wallMaterial?.name,
    nameIt: wallMaterial?.nameIt ?? null,
    nameDe: wallMaterial?.nameDe ?? null,
    nameEs: wallMaterial?.nameEs ?? null,
    nameFr: wallMaterial?.nameFr ?? null,
    order: wallMaterial?.order ?? 50,
    visibility: wallMaterial?.visibility,
  };

  const submit = async (body: UpdateWallMaterialNewImageBody | UpdateWallMaterialRemoveImageBody) => {
    try {
      await wallMaterialApi.updateAdminWallMaterial(wallMaterial?.id!, body);
      onSave();
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = () => async (formData: UpdateWallMaterialForm) => {
    setIsSubmitting(true);

    const commonBody = {
      name: formData.name?.trim()!,
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      order: formData.order!,
      installationTypeId: wallMaterial?.installationType.id!,
      visibility: formData.visibility,
    };

    if (newImage) {
      try {
        const signedUrl = await wallMaterialApi.generateUploadImageUrl(wallMaterial?.id!, newImage.name, newImage.type);
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsSubmitting(false);
          return;
        }
        const body: UpdateWallMaterialNewImageBody = {
          ...commonBody,
          image: newImage.name,
        };
        await submit(body);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const body: UpdateWallMaterialRemoveImageBody = {
        ...commonBody,
        removeImage: removeImage,
      };
      await submit(body);
    }
  };

  return (
    <Form<UpdateWallMaterialForm>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [wallMaterial]);

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
        }, [register]);

        const values = watch();

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="name"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.name}
                  >
                    <Input
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled}
                      {...register("name", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default WallMaterialDetails;
